import React, { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../U3204';
import type { U3204ConversationSearchRow } from '../U3204Service';
import { u3204MoveIdInList, u3204ReorderIds } from '../utils/u3204PinnedReorder';
import { u3204TruncateConversationGroup, U3204_CONVERSATION_GROUP_MAX_VISIBLE } from '../utils/u3204TruncateConversationGroup';
import U3204ConversationListItem from './U3204ConversationListItem';

interface U3204ConversationGroupSectionProps {
  bucketLabelKey?: string;
  showHeading?: boolean;
  showMissionHint?: boolean;
  conversations: U3204ConversationSearchRow[];
  activeConversationId: string | null;
  variant: 'active' | 'archived';
  expanded: boolean;
  enableReorder?: boolean;
  reordering?: boolean;
  onToggleExpanded: () => void;
  onSelect: (id: string) => void;
  onRename?: (conversation: U3204ConversationSearchRow) => void;
  onPin?: (conversation: U3204ConversationSearchRow) => void;
  onUnpin?: (conversation: U3204ConversationSearchRow) => void;
  onReorderPinned?: (nextVisibleOrderedIds: string[]) => void;
  onArchive?: (conversation: U3204ConversationSearchRow) => void;
  onUnarchive?: (conversation: U3204ConversationSearchRow) => void;
  onDelete: (conversation: U3204ConversationSearchRow) => void;
}

const U3204ConversationGroupSection: React.FC<U3204ConversationGroupSectionProps> = ({
  bucketLabelKey,
  showHeading = true,
  showMissionHint = false,
  conversations,
  activeConversationId,
  variant,
  expanded,
  enableReorder = false,
  reordering = false,
  onToggleExpanded,
  onSelect,
  onRename,
  onPin,
  onUnpin,
  onReorderPinned,
  onArchive,
  onUnarchive,
  onDelete,
}) => {
  const { t } = useTranslation(NS);
  const truncated = u3204TruncateConversationGroup(conversations, expanded);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const dragFromIndexRef = useRef<number | null>(null);

  if (conversations.length === 0) {
    return null;
  }

  const visibleOrderedIds = truncated.visible.map((c) => c.id);
  const trailingOrderedIds = conversations
    .map((c) => c.id)
    .filter((id) => !visibleOrderedIds.includes(id));

  const emitReorder = (nextVisibleIds: string[]) => {
    onReorderPinned?.([...nextVisibleIds, ...trailingOrderedIds]);
  };

  const handleDragStart = (index: number) => (event: DragEvent) => {
    if (!enableReorder || reordering) return;
    dragFromIndexRef.current = index;
    setDraggingIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index: number) => (event: DragEvent) => {
    if (!enableReorder || reordering) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dropTargetIndex !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDragEnd = () => {
    dragFromIndexRef.current = null;
    setDraggingIndex(null);
    setDropTargetIndex(null);
  };

  const handleDrop = (toIndex: number) => (event: DragEvent) => {
    event.preventDefault();
    const fromIndex = dragFromIndexRef.current;
    handleDragEnd();
    if (!enableReorder || fromIndex == null || fromIndex === toIndex) {
      return;
    }
    const nextVisibleIds = u3204ReorderIds(visibleOrderedIds, fromIndex, toIndex);
    if (nextVisibleIds === visibleOrderedIds) {
      return;
    }
    emitReorder(nextVisibleIds);
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    if (!enableReorder || reordering) return;
    const nextVisibleIds = u3204MoveIdInList(visibleOrderedIds, index, direction);
    if (nextVisibleIds === visibleOrderedIds) {
      return;
    }
    emitReorder(nextVisibleIds);
  };

  return (
    <section>
      {showHeading && bucketLabelKey ? (
        <h3 className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t(bucketLabelKey, { ns: NS })}
        </h3>
      ) : null}
      <ul className="space-y-0.5">
        {truncated.visible.map((conv, index) => (
          <li
            key={conv.id}
            onDragOver={enableReorder ? handleDragOver(index) : undefined}
            onDrop={enableReorder ? handleDrop(index) : undefined}
            className={`rounded-lg transition-opacity ${
              enableReorder && draggingIndex === index ? 'opacity-60' : ''
            } ${
              enableReorder && dropTargetIndex === index && draggingIndex !== index
                ? 'ring-2 ring-primary/40 ring-offset-1'
                : ''
            }`}
          >
            <div className="flex items-start gap-0.5">
              {enableReorder ? (
                <span
                  role="button"
                  tabIndex={reordering ? -1 : 0}
                  draggable={!reordering}
                  onDragStart={handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  className="mt-1.5 inline-flex h-7 w-6 shrink-0 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
                  title={t('dragHint', { ns: NS })}
                  aria-label={t('dragHint', { ns: NS })}
                >
                  <span aria-hidden="true" className="select-none text-xs leading-none">
                    ⋮⋮
                  </span>
                </span>
              ) : null}
              <div className="min-w-0 flex-1">
                <U3204ConversationListItem
                  conversation={conv}
                  isActive={activeConversationId === conv.id}
                  variant={variant}
                  showMissionHint={showMissionHint}
                  enableReorder={enableReorder}
                  canMoveUp={enableReorder && index > 0 && !reordering}
                  canMoveDown={
                    enableReorder && index < truncated.visible.length - 1 && !reordering
                  }
                  onSelect={onSelect}
                  onRename={onRename}
                  onPin={onPin}
                  onUnpin={onUnpin}
                  onMoveUp={() => handleMove(index, 'up')}
                  onMoveDown={() => handleMove(index, 'down')}
                  onArchive={onArchive}
                  onUnarchive={onUnarchive}
                  onDelete={onDelete}
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
      {truncated.hasMore ? (
        <button
          type="button"
          onClick={onToggleExpanded}
          className="mt-1 w-full px-2 py-1 text-left text-xs font-medium text-primary hover:underline"
        >
          {t('seeMore', { ns: NS, count: truncated.hiddenCount })}
        </button>
      ) : null}
      {expanded && conversations.length > U3204_CONVERSATION_GROUP_MAX_VISIBLE ? (
        <button
          type="button"
          onClick={onToggleExpanded}
          className="mt-1 w-full px-2 py-1 text-left text-xs font-medium text-muted-foreground hover:underline"
        >
          {t('seeLess', { ns: NS })}
        </button>
      ) : null}
    </section>
  );
};

export default U3204ConversationGroupSection;
